import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { Button, Card, Input, Row, Text } from "@nextui-org/react";
import React, { useCallback, useMemo, useState } from "react";
import { Icon, Link } from "../components";
import { useResumeDataContext } from "../context";
import { ago, DATE_FORMATS, formatDate } from "../helpers";
import { useDebounce } from "../hooks";
import { useCsv } from "../hooks/use-csv";
import { useResumeRouting } from "../routing";
import styles from "./resume-manager.module.less";
export var ResumeManager = function ResumeManager() {
  var _useResumeDataContext = useResumeDataContext(),
      deleteResume = _useResumeDataContext.deleteResume,
      newResume = _useResumeDataContext.newResume,
      resumes = _useResumeDataContext.resumeEntries,
      updateResumeEntry = _useResumeDataContext.updateResumeEntry;

  useResumeRouting();
  var onRenameResume = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(resumeId, currentDisplayName) {
      var displayName;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              displayName = prompt("Please enter a name for this resume", currentDisplayName);

              if (displayName) {
                _context.next = 3;
                break;
              }

              return _context.abrupt("return");

            case 3:
              _context.next = 5;
              return updateResumeEntry(resumeId, {
                displayName: displayName
              });

            case 5:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x, _x2) {
      return _ref.apply(this, arguments);
    };
  }(), [updateResumeEntry]);
  var onDeleteResume = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(resumeId, currentDisplayName) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (confirm("Are you sure you want to delete \"".concat(currentDisplayName || resumeId, "?\""))) {
                _context2.next = 2;
                break;
              }

              return _context2.abrupt("return");

            case 2:
              _context2.next = 4;
              return deleteResume(resumeId);

            case 4:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function (_x3, _x4) {
      return _ref2.apply(this, arguments);
    };
  }(), [deleteResume]);

  var _useState = useState(""),
      _useState2 = _slicedToArray(_useState, 2),
      searchText = _useState2[0],
      setSearchText = _useState2[1];

  var onSearch = useDebounce(setSearchText);
  var filteredResumes = useMemo(function () {
    if (!resumes.length) return resumes;
    if (!searchText.trim()) return resumes;
    var words = searchText.trim().toLowerCase().split(/\s+/g);
    var filtered = resumes.filter(function (_ref3) {
      var displayName = _ref3.displayName,
          id = _ref3.id;
      return words.every(function (word) {
        return (displayName === null || displayName === void 0 ? void 0 : displayName.toLowerCase().includes(word)) || id.toLowerCase().includes(word);
      });
    });
    return filtered;
  }, [resumes, searchText]); // reporting

  var columnNames = useMemo(function () {
    return ["id", "displayName", "theme", "created", "modified", "views"];
  }, []);

  var _useCsv = useCsv(resumes, columnNames),
      runReport = _useCsv.run;

  if (!resumes.length) {
    return /*#__PURE__*/React.createElement(Card, {
      color: "warning"
    }, /*#__PURE__*/React.createElement(Text, {
      h1: true
    }, "No Resumes Found"), /*#__PURE__*/React.createElement(Text, null, "You don't have any resumes"), /*#__PURE__*/React.createElement(Button, {
      onClick: newResume
    }, "Create one"));
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Row, {
    gap: 2
  }, /*#__PURE__*/React.createElement(Text, {
    h1: true
  }, "Resume Manager")), /*#__PURE__*/React.createElement(Row, {
    gap: 2,
    css: {
      overflowX: "auto"
    }
  }, /*#__PURE__*/React.createElement("table", {
    className: styles.table,
    style: {
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: 4
  }, /*#__PURE__*/React.createElement(Input, {
    clearable: true,
    contentLeft: /*#__PURE__*/React.createElement(Icon, {
      name: "search"
    }),
    css: {
      maxWidth: "300px"
    },
    fullWidth: true,
    onChange: function onChange(e) {
      onSearch(e.target.value);
    },
    status: "primary"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Button, {
    onClick: runReport
  }, "Export CSV"))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Name"), /*#__PURE__*/React.createElement("th", null, "Created"), /*#__PURE__*/React.createElement("th", null, "Modified"), /*#__PURE__*/React.createElement("th", null, "Views"), /*#__PURE__*/React.createElement("th", null))), /*#__PURE__*/React.createElement("tbody", null, !filteredResumes.length && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: 5
  }, "No resumes matched your search")), filteredResumes.map(function (entry) {
    return /*#__PURE__*/React.createElement("tr", {
      key: entry.id
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Link, {
      to: "../interactive/".concat(entry.id)
    }, entry.displayName || /*#__PURE__*/React.createElement("em", null, "Untitled resume"))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      title: formatDate(entry.created, DATE_FORMATS.NORMAL)
    }, ago(entry.created))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      title: formatDate(entry.modified, DATE_FORMATS.NORMAL)
    }, entry.modified ? ago(entry.modified) : "Never")), /*#__PURE__*/React.createElement("td", null, entry.views || 0), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
      className: styles.actions
    }, /*#__PURE__*/React.createElement(Button, {
      auto: true,
      size: "sm",
      onClick: function onClick() {
        onRenameResume(entry.id, entry.displayName);
      },
      title: "Rename"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "pen-to-square"
    })), /*#__PURE__*/React.createElement(Button, {
      auto: true,
      size: "sm",
      onClick: function onClick() {
        onDeleteResume(entry.id, entry.displayName);
      },
      title: "Delete"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "trash-can"
    })))));
  })))));
};